import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';

/* Inviting somebody in.

   The API for this existed from the start and had no interface at all, so the
   one thing members actually said they wanted to do — bring people they know —
   was impossible. An invite is a link, not a code to copy across by hand: the
   code is already filled in when they open it.

   Binding an invite to an email is optional but better. A loose code works for
   whoever holds it; a bound one only works for the person it was meant for, so
   a forwarded link cannot be used by someone else. */

export default function Invites({ notify }) {
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [credit, setCredit] = useState('3');
  const [days, setDays] = useState('14');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [tempEmail, setTempEmail] = useState('');
  const [temp, setTemp] = useState(null);

  const load = useCallback(() => {
    api.invites().then(setInvites).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.me().then((me) => setIsAdmin(Boolean(me.is_admin))).catch(() => {});
  }, [load]);

  async function create() {
    setBusy(true);
    try {
      const invite = await api.createInvite(isAdmin
        ? { email: email.trim(), credit_usd: Number(credit) || 0, expires_days: Number(days) || 14 }
        : { email: email.trim() });
      setEmail('');
      if (invite.email && sendEmail) {
        try {
          const r = await api.emailInvite(invite.code);
          await copy(invite.url, r?.sent ? `Invite emailed to ${invite.email} — link copied too`
            : `Invite email to ${invite.email} is queued and should arrive shortly — link copied too`);
        } catch (err) {
          await copy(invite.url, `Invite created and link copied, but the email didn't send: ${err.message}`);
        }
      } else {
        await copy(invite.url, 'Invite created — link copied');
      }
      load();
    } catch (err) {
      notify?.(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function makeTempPassword() {
    if (!window.confirm(`Give ${tempEmail.trim()} a new temporary password? Their current password stops working.`)) return;
    setBusy(true);
    try {
      setTemp(await api.tempPassword(tempEmail.trim()));
      setTempEmail('');
    } catch (err) {
      notify?.(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function copy(url, message) {
    try {
      await navigator.clipboard.writeText(url);
      notify?.(message || 'Link copied');
    } catch {
      // Clipboard is blocked outside a secure context or without permission.
      // Showing the link beats silently doing nothing.
      window.prompt('Copy this:', url);
    }
  }

  const unused = invites.filter((i) => !i.redeemed && !i.expired);
  const used = invites.filter((i) => i.redeemed);
  const expired = invites.filter((i) => i.expired);
  const until = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '');

  return (
    <section className="invites">
      <div className="invites-head">
        <h2 className="sec-title">Invite someone ({unused.length} open)</h2>
        <button className="btn ghost sm" onClick={() => setOpen(!open)}>
          {open ? 'Close' : 'New invite'}
        </button>
      </div>

      {open && (
        <div className="card pad invite-new">
          <p className="muted-sm">
            Each invite works once. Add their email and it only works for that email address —
            safer if it might get forwarded. Unused invites expire.
          </p>
          {isAdmin && (
            <div className="invite-admin">
              <label>Starting credit $<input className="in sm" type="number" min="0" max="50" step="1"
                value={credit} onChange={(e) => setCredit(e.target.value)} /></label>
              <label>Expires in <input className="in sm" type="number" min="1" max="90"
                value={days} onChange={(e) => setDays(e.target.value)} /> days</label>
            </div>
          )}
          <div className="invite-row">
            <input
              className="in"
              type="email"
              value={email}
              placeholder="their@email.com (optional)"
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !busy && create()}
            />
            <button className="btn primary" onClick={create} disabled={busy}>
              {busy ? 'Creating…' : email.trim() && sendEmail ? 'Create & email' : 'Create link'}
            </button>
          </div>
          {email.trim() && (
            <label className="muted-sm invite-send">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
              {' '}Email the invite to them (you'll also get the link to send yourself)
            </label>
          )}
        </div>
      )}

      {invites.length === 0 && !open && (
        <p className="muted-sm">
          Nobody invited yet. The network is only worth as much as who is in it.
        </p>
      )}

      {unused.map((i) => (
        <div className="invite-item" key={i.code}>
          <div className="invite-main">
            <b>{i.email || 'Anyone with the link'}</b>
            <code className="invite-code">{i.code}</code>
            <span className="muted-sm">
              {i.credit_usd > 0 ? `$${i.credit_usd.toFixed(0)} credit · ` : ''}until {until(i.expires_at)}
              {isAdmin && i.created_by ? ` · by ${i.created_by}` : ''}
            </span>
          </div>
          <div className="invite-actions">
            {i.email && (
              <button className="btn quiet sm" onClick={() => api.emailInvite(i.code)
                .then((r) => notify?.(r?.sent ? `Invite emailed to ${i.email}` : `Queued — the email to ${i.email} should arrive shortly`)).catch((e) => notify?.(e.message))}>
                Email it
              </button>
            )}
            <button className="btn quiet sm" onClick={() => copy(i.url)}>
              Copy link
            </button>
          </div>
        </div>
      ))}

      {used.map((i) => (
        <div className="invite-item is-used" key={i.code}>
          <div className="invite-main">
            <b>{i.redeemed_by || i.email || 'Someone'}</b>
            <span className="muted-sm">
              joined{i.credit_usd > 0 ? (i.credit_granted ? ` · $${i.credit_usd.toFixed(0)} credit added` : ' · credit pending') : ''}
              {isAdmin && i.created_by ? ` · invited by ${i.created_by}` : ''}
            </span>
          </div>
        </div>
      ))}

      {isAdmin && (
        <div className="card pad temp-pw">
          <b>Temporary password</b>
          <p className="muted-sm">
            For a member who forgot their password, or never had one. Shown once — pass it on
            yourself. They sign in with their email and change it under Profile → Password.
          </p>
          <div className="invite-row">
            <input className="in" type="email" value={tempEmail} placeholder="member@email.com"
              onChange={(e) => setTempEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !busy && tempEmail.trim() && makeTempPassword()} />
            <button className="btn ghost" onClick={makeTempPassword} disabled={busy || !tempEmail.trim()}>
              Create
            </button>
          </div>
          {temp && (
            <div className="temp-pw-out">
              <span className="muted-sm">{temp.name} ({temp.email}):</span>
              <code className="invite-code">{temp.temp_password}</code>
              <button className="btn quiet sm" onClick={() => copy(temp.temp_password, 'Password copied')}>Copy</button>
              <button className="btn quiet sm" onClick={() => setTemp(null)}>Done</button>
            </div>
          )}
        </div>
      )}

      {expired.length > 0 && (
        <p className="muted-sm">{expired.length} invite{expired.length > 1 ? 's' : ''} expired unused.</p>
      )}
    </section>
  );
}
